import type { Span } from '@opentelemetry/api'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-web'
import { TracingInstrumentation } from '@grafana/faro-web-tracing'
import { getCurrentFaroUserContext } from 'services/faroUserContext'
import { getTraceHeaderCorsUrls } from 'services/getTraceHeaderCorsUrls'

export const applyFaroUserContextToSpan = (span: Span): void => {
    const { userId, classId } = getCurrentFaroUserContext()
    if (userId) {
        span.setAttribute('user.id', userId)
    }
    if (classId) {
        span.setAttribute('classroom.id', classId)
    }
}

const applyCustomAttributesOnSpan = (span: Span): void => {
    applyFaroUserContextToSpan(span)
}

export interface FaroTracingEnv {
    REACT_APP_FARO_TRACE_URL?: string
}

const TRACING_BATCH_DELAY_MS = 1000
const TRACING_MAX_EXPORT_BATCH_SIZE = 30

const getFaroTracingEnvFromProcess = (): FaroTracingEnv => ({
    REACT_APP_FARO_TRACE_URL: process.env.REACT_APP_FARO_TRACE_URL
})

export const getFaroTraceExportUrl = (
    env: FaroTracingEnv = getFaroTracingEnvFromProcess()
): string | undefined => {
    const url = env.REACT_APP_FARO_TRACE_URL?.trim()
    return url || undefined
}

export const usesSeparateFaroTraceExport = (
    env: FaroTracingEnv = getFaroTracingEnvFromProcess()
): boolean => Boolean(getFaroTraceExportUrl(env))

export const createOtlpTraceSpanProcessor = (traceExportUrl: string): BatchSpanProcessor =>
    new BatchSpanProcessor(
        new OTLPTraceExporter({ url: traceExportUrl }),
        {
            scheduledDelayMillis: TRACING_BATCH_DELAY_MS,
            maxExportBatchSize: TRACING_MAX_EXPORT_BATCH_SIZE
        }
    )

export const buildTracingInstrumentation = (
    env: FaroTracingEnv = getFaroTracingEnvFromProcess()
): TracingInstrumentation => {
    const traceExportUrl = getFaroTraceExportUrl(env)
    const instrumentationOptions = {
        propagateTraceHeaderCorsUrls: getTraceHeaderCorsUrls(),
        fetchInstrumentationOptions: {
            applyCustomAttributesOnSpan
        },
        xhrInstrumentationOptions: {
            applyCustomAttributesOnSpan
        }
    }

    if (!traceExportUrl) {
        return new TracingInstrumentation({ instrumentationOptions })
    }

    return new TracingInstrumentation({
        instrumentationOptions,
        spanProcessor: createOtlpTraceSpanProcessor(traceExportUrl)
    })
}
